import { useMemo } from "react";
import {
  CanAccess,
  useGetIdentity,
  useGetList,
  useGetMany,
  useTranslate,
  type Identifier,
} from "ra-core";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";

import {
  getTeamRosterClaimAuthorization,
  type Actor,
} from "../providers/commons/canAccess";
import {
  ListEmptyState,
  ListErrorState,
  ListRowsSkeleton,
} from "../design/ListStates";
import type {
  AppRole,
  BusinessArm,
  Project,
  Sale,
  TeamRosterEntry,
  TeamRosterProject,
} from "../types";
import { RosterClaimRow } from "./RosterClaimRow";

const EMPTY_VALUE = "—";

/**
 * Fetches every pending roster claim in one filtered query, plus the small
 * fixed set of batched lookups (claimant `sales` rows, their acompañamiento
 * roster names, and their seeded project memberships/names) the section
 * needs to render -- never one request per row (the ticket's `performance`
 * non-functional requirement).
 */
const usePendingRosterClaimsData = () => {
  const {
    data: pendingClaims,
    isPending: isClaimsPending,
    error: claimsError,
    refetch: refetchClaims,
  } = useGetList<TeamRosterEntry>("team_roster", {
    pagination: { page: 1, perPage: 100 },
    sort: { field: "claimed_at", order: "ASC" },
    filter: { match_status: "pending" },
  });

  const claims = pendingClaims ?? [];
  const claimantIds = useMemo(
    () =>
      Array.from(
        new Set(
          claims
            .map((claim) => claim.sales_id)
            .filter((id): id is Identifier => id != null),
        ),
      ),
    [claims],
  );
  const accompanimentIds = useMemo(
    () =>
      Array.from(
        new Set(
          claims
            .map((claim) => claim.reports_to_roster_id)
            .filter((id): id is Identifier => id != null),
        ),
      ),
    [claims],
  );
  const rosterIds = useMemo(() => claims.map((claim) => claim.id), [claims]);

  const { data: claimants } = useGetMany<Sale>(
    "sales",
    { ids: claimantIds },
    { enabled: claimantIds.length > 0 },
  );
  const { data: accompaniments } = useGetMany<TeamRosterEntry>(
    "team_roster",
    { ids: accompanimentIds },
    { enabled: accompanimentIds.length > 0 },
  );
  const { data: memberships } = useGetList<TeamRosterProject>(
    "team_roster_projects",
    {
      pagination: { page: 1, perPage: 1000 },
      sort: { field: "team_roster_id", order: "ASC" },
      // PostgREST/ra-data-postgrest `@in` expects the string form
      // `(id1,id2,...)`, not an array -- see activity.ts's
      // `sales_id@in` usage. team_roster_projects has no `id` column
      // (composite PK team_roster_id/project_id), so sort by an
      // existing column instead.
      filter: { "team_roster_id@in": `(${rosterIds.join(",")})` },
    },
    { enabled: rosterIds.length > 0 },
  );
  const projectIds = useMemo(
    () =>
      Array.from(
        new Set((memberships ?? []).map((membership) => membership.project_id)),
      ),
    [memberships],
  );
  const { data: projects } = useGetMany<Project>(
    "projects",
    { ids: projectIds },
    { enabled: projectIds.length > 0 },
  );

  return {
    claims,
    isClaimsPending,
    claimsError,
    refetchClaims,
    claimants: claimants ?? [],
    accompaniments: accompaniments ?? [],
    memberships: memberships ?? [],
    projects: projects ?? [],
  };
};

const PendingRosterClaimsList = () => {
  const translate = useTranslate();
  const { identity } = useGetIdentity();
  const {
    claims,
    isClaimsPending,
    claimsError,
    refetchClaims,
    claimants,
    accompaniments,
    memberships,
    projects,
  } = usePendingRosterClaimsData();

  const actor: Actor | undefined = identity
    ? {
        role: identity.role as AppRole,
        id: identity.id,
        business_arm: (identity.business_arm ?? null) as BusinessArm | null,
      }
    : undefined;

  if (!actor || isClaimsPending) {
    return <ListRowsSkeleton rows={2} columns={2} />;
  }

  if (claimsError) {
    return (
      <ListErrorState
        message={translate("crm.common.list_error")}
        onRetry={() => refetchClaims()}
      />
    );
  }

  const visibleClaims = claims.filter((claim) => {
    const claimant = claimants.find((sale) => sale.id === claim.sales_id);
    return getTeamRosterClaimAuthorization(
      actor,
      claim,
      claimant
        ? {
            id: claimant.id,
            role: claimant.role,
            business_arm: claimant.business_arm ?? null,
          }
        : undefined,
    ).visible;
  });

  if (visibleClaims.length === 0) {
    return (
      <ListEmptyState
        title={translate("resources.sales.pending_claims.empty_title")}
        description={translate(
          "resources.sales.pending_claims.empty_description",
        )}
      />
    );
  }

  return (
    <div className="flex flex-col gap-3">
      {visibleClaims.map((claim) => {
        const claimant = claimants.find((sale) => sale.id === claim.sales_id);
        const accompaniment = accompaniments.find(
          (entry) => entry.id === claim.reports_to_roster_id,
        );
        const claimProjects = memberships
          .filter((membership) => membership.team_roster_id === claim.id)
          .map((membership) => {
            const project = projects.find(
              (p) => p.id === membership.project_id,
            );
            return {
              name: project?.name ?? EMPTY_VALUE,
              isLider: membership.is_lider_for_project,
            };
          });

        return (
          <RosterClaimRow
            key={claim.id}
            actor={actor}
            claim={claim}
            claimant={claimant}
            accompanimentName={accompaniment?.full_name}
            projects={claimProjects}
          />
        );
      })}
    </div>
  );
};

/**
 * "Claves por confirmar" section (Almikia Epic E3b-2, TASK-085): visible
 * only to direccion and lead_manager (`canAccess("team_roster", "list")`);
 * every other role never sees it, and the confirm/reject RPCs refuse them
 * anyway. Lists every pending claim the viewer may act on -- a lead_manager
 * only sees claims within her brazo scope, and a role-changing claim shows
 * as "only Dirección puede confirmarla" with no enabled action
 * (`getTeamRosterClaimAuthorization`, shared with both data providers).
 */
export const PendingRosterClaims = () => {
  const translate = useTranslate();

  return (
    <CanAccess resource="team_roster" action="list">
      <Card>
        <CardHeader>
          <CardTitle>
            {translate("resources.sales.pending_claims.title")}
          </CardTitle>
        </CardHeader>
        <CardContent>
          <PendingRosterClaimsList />
        </CardContent>
      </Card>
    </CanAccess>
  );
};
